import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";
import ApiServices from "../../../Utils/ApiServices/ApiServices";
import { getDecryptedAccessToken, getDecryptedData, getDecryptedUserType } from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";
import { useParams } from "react-router-dom";
import { AccessTime, Note, Person } from "@mui/icons-material";

const SoapNotesComponent = ({ serviceId ,appointmentData}) => {
  const accessToken = getDecryptedAccessToken();
  const LoginUserType = getDecryptedUserType();
  const [soapNotes, setSoapNotes] = useState([]);
  const [soapNote, setSoapNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const LoginUserDetails = getDecryptedData();
  const params = useParams();
  const eid = params.eid || appointmentData?.pc_eid;
  const pid = params.pid || appointmentData?.pc_pid;


  useEffect(() => {
    if (pid && eid && serviceId) {
      fetchSoapNotes();
    }
  }, [pid, eid, serviceId]);

  const fetchSoapNotes = async () => {
    try {
      const response = await ApiServices.get(
        `/apis/default/api/soapnote/${pid}/appointment/${eid}`,
        { Authorization: `Bearer ${accessToken}` }
      );

      if (response.data && Array.isArray(response.data)) {
        const notes = response.data.reduce((acc, entry) => {
          if (entry.service_id === serviceId && entry.soap_note && entry.soap_note.soap_note) {
            return Object.values(entry.soap_note.soap_note);
          }
          return acc;
        }, []);

        setSoapNotes(notes);
        if (notes.length > 0) {
          setSoapNote(notes[notes.length - 1].Note);
        }
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Error fetching SOAP notes", severity: "error" });
    }
  };

  const handleSaveSoapNote = async () => {
    if (!soapNote.trim()) return;
    setSaving(true);
    try {
      const payload = {
        service_id: serviceId,
        eid: eid,
        pid: pid,
        soap_note: [{ Timestamp: new Date().toISOString(), Note: soapNote, ProviderID: LoginUserDetails.id }],
      };

      await ApiServices.post("/apis/default/api/soapnote", payload, { Authorization: `Bearer ${accessToken}` });
      fetchSoapNotes();
      setSnackbar({ open: true, message: "SOAP note saved successfully!", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to save SOAP note", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date
      .getDate()
      .toString()
      .padStart(2, "0")} ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes()
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <Box mt={2}>
      <Typography variant="subtitle1" fontWeight="bold">SOAP Notes:</Typography>
      {soapNotes.length > 0 ? (
        <Grid container spacing={1} sx={{ mt: 1 }}>
          {soapNotes.map((note, index) => (
            <Grid container item key={index} sx={{ backgroundColor: "#f9f9f9", borderRadius: 1, p: 1.5, mb: 1 }}>
              <Grid item sm={9} display="flex" alignItems="center">
                <Note sx={{ mr: 1, color: "#1976d2" }} />
                <Typography variant="body1">{note.Note}</Typography>
              </Grid>
              <Grid item sm={2} display="flex" alignItems="center">
                <AccessTime sx={{ mr: 1, color: "#ff9800" }} />
                <Typography variant="body2">{formatTimestamp(note.Timestamp)}</Typography>
              </Grid>
              <Grid item sm={1} display="flex" alignItems="center">
                <Person sx={{ mr: 1, color: "#4caf50" }} />
                <Typography variant="caption">{note.ProviderID}</Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography variant="body2" color="textSecondary">No SOAP notes available.</Typography>
      )}

      {LoginUserType !== "patient" && (
        <Box mt={3} sx={{ borderTop: "1px solid #ddd", pt: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">Add / Edit SOAP Note:</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder="Write SOAP note here..."
            value={soapNote}
            onChange={(e) => setSoapNote(e.target.value)}
            sx={{ backgroundColor: "#f9f9f9", borderRadius: 1 }}
          />
          <Box mt={1} textAlign="right">
            <Button variant="contained" color="primary" onClick={handleSaveSoapNote} disabled={saving}>
              {saving ? "Saving..." : "Save SOAP Note"}
            </Button>
          </Box>
        </Box>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SoapNotesComponent;
